import React, {useState} from "react";
import {Alert, AlertTitle, Button, Menu, MenuItem, Stack, Step, StepLabel, Stepper, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import {SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import {useNavigate, useParams} from "react-router-dom";
import NewServiceCommonData from "./NewServiceCommonData";
import {ServiceService} from "crqlar-react-common/dist/services/service/service.service";
import AvailabilityPlanner from "crqlar-react-common/dist/components/availability/AvailabilityPlanner";
import ScreenWrapper from "crqlar-react-common/dist/components/ScreenWrapper";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import {LoadingButton} from "@mui/lab";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {useCurrentApp} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {useSector} from "crqlar-react-common/dist/utils/hooks/sector/useSectors";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {isServiceValid, prepareServiceForSave} from "src/utils/serviceSave.util";

const CreateNewService = () => {

    useTrackScreenVisit('create new service')
    const {t} = useTranslation();
    const {entityId} = useParams()
    const navigate = useNavigate()
    const appData = useCurrentApp()
    const sector = useSector(appData?.configuration?.sectorId)

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    //steps
    const [activeStep, setActiveStep] = useState(0);

    const [uiState, setUiState] = useState<'create' | 'done'>('create')

    const serviceData = useEntity({
        get: async () => {
            return ServiceService.getServiceById(entityId!)
        },
        create: ServiceService.createService,
        update: ServiceService.updateService,
        delete: (id) => ServiceService.deleteService({id: [id]}),
        onDeleted: () => navigate(-1),
        isValid: isServiceValid,
        cleanupBeforeSave: prepareServiceForSave
    }, entityId)

    const handleNext = async () => {
        if (activeStep === 0) {
            await serviceData.save()
        }

        if (activeStep === steps.length - 1) {
            await serviceData.saveThenSet({
                ...serviceData.item,
                meta: {
                    ...serviceData.item.meta,
                    isTemporary: false
                }
            })
            setUiState('done')
        } else {
            setActiveStep(activeStep + 1);
        }
    };

    const handleBack = () => {
        if (activeStep === 0) {
            navigate(-1)
            return
        }
        setActiveStep(activeStep - 1);
    };


    let steps: string[] = []
    switch (sector?.sectorType) {
        case SectorType.RESTAURANT:
            steps = ['general', 'availability']
            break;
        default:
            steps = ['general', 'availability']
            break;
    }

    let stepName = (step: string) => t(`New service step ${step}`)
    const stepNames = steps.map(stepName)


    const navigationUI = (
        <Stack
            direction="row"
            gap={2}
            alignSelf={'center'}
            mt={2}
        >

            <Button
                variant={'text'}
                disabled={activeStep === 0}
                onClick={handleBack}
                startIcon={<ChevronLeftIcon/>}
            >
                {t('Back')}
            </Button>

            <LoadingButton
                variant={'contained'}
                onClick={handleNext}
                endIcon={<ChevronRightIcon/>}
                loading={serviceData.saving}
                disabled={!serviceData.isValid}
            >
                {activeStep === steps.length - 1 ? t('Finish setup') : t(`Continue to ${steps[activeStep + 1]}`)}
            </LoadingButton>

            <Button
                variant={'text'}
                onClick={handleClick}
                endIcon={<MoreVertIcon/>}
            >
                {t('More')}
            </Button>

            <Menu
                id={`more-menu`}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
            >
                {
                    [{
                        title: t('Delete'),
                        onClick: async () => {
                            handleClose()
                            await serviceData.remove()
                        }
                    }].map((item, index) => (
                        <MenuItem
                            key={index}
                            onClick={item.onClick}
                        >
                            {item.title}
                        </MenuItem>
                    ))
                }
            </Menu>

        </Stack>
    )

    let ui
    switch (steps[activeStep]) {
        case 'general':
            ui = (
                <Stack>
                    <NewServiceCommonData
                        service={serviceData.item}
                        setService={serviceData.setItem}
                    />
                    {navigationUI}
                </Stack>
            )
            break;
        case 'availability':
            ui = (
                <Stack width={'100%'}>
                    <AvailabilityPlanner/>
                    {navigationUI}
                </Stack>
            )
            break;
    }

    const doneUi = (
        <Stack
            sx={{
                gap: 2,
                height: 400,
                maxWidth: 1300,
                mt: 4,
                width: '100%',
                alignItems: 'center'
            }}
        >

            <CheckCircleIcon
                sx={{
                    fontSize: 75,
                }}
            />

            <Typography variant={'h2'}>
                {t('Service created')}
            </Typography>

            <Alert
                severity={'info'}
            >
                <AlertTitle>{t('Tip')}</AlertTitle>
                <Typography>
                    {t(`You can edit service details or do advanced settings in the service details page`, {name: serviceData.item.name})}
                </Typography>
                <Button
                    onClick={() => {
                        navigate(`/system/services/${serviceData.item.id}`)
                    }}
                    size={'small'}
                    startIcon={<OpenInNewIcon/>}
                >
                    {t('Open service details')}
                </Button>
            </Alert>

            <Button
                variant={'contained'}
                onClick={() => {
                    navigate(-1)
                    setUiState('create')
                }}
                startIcon={<ChevronLeftIcon/>}
            >
                {t('Back to overview')}
            </Button>
        </Stack>
    )

    let uiComponent
    switch (uiState) {
        case 'create':
            uiComponent = ui
            break;
        case 'done':
            uiComponent = doneUi
            break;
    }

    return (
        <ScreenWrapper>
            <Stack
                gap={2}
                maxWidth={1300}
                width={'100%'}
                alignSelf={'center'}
            >
                <Stepper activeStep={activeStep}>
                    {stepNames.map((label, index) => {
                        const stepProps: { completed?: boolean } = {};
                        const labelProps: {
                            optional?: React.ReactNode;
                        } = {};
                        return (
                            <Step key={label} {...stepProps}>
                                <StepLabel {...labelProps}>{label}</StepLabel>
                            </Step>
                        );
                    })}
                </Stepper>
                {uiComponent}
            </Stack>

            {serviceData.renderSnackbars()}

        </ScreenWrapper>
    )


}
export default CreateNewService;
